import { AsyncJobResponse, OrgDocResponse } from '@/models/Orgs'
import { UUID } from 'crypto'

import {
  WebsiteUploadResult,
  uploadFilesFn,
  uploadWebsitesFn,
  uploadFundingNeedFilesFn,
  uploadFundingNeedWebsitesFn,
} from '@/models/Orgs'

const serverURL = import.meta.env.VITE_API_URL
const UPLOAD_JOB_TIMEOUT_MS = 20 * 60 * 1000
const POLL_INTERVAL_MS = 1000
// A crawl takes minutes; once it clearly is one, stop asking every second.
const POLL_BACKOFF_AFTER_MS = 30 * 1000
const POLL_INTERVAL_SLOW_MS = 5000
// Tolerate a transient blip without abandoning a job that is still running.
// Time-based rather than a count: five one-second failures is ~5s, which a proxy
// restart early in a multi-minute crawl would blow straight through.
const POLL_FAILURE_GRACE_MS = 60 * 1000

const GENERIC_UPLOAD_ERROR =
  'Error uploading files. Please check your connection and try again.'

/** We stopped being able to watch the job — its outcome is genuinely unknown.
 *
 * Distinct from a failure on purpose: the upload is fire-and-forget, so the
 * worker carries on and the docs still land. Callers must not mark pages failed
 * on this. */
export const UPLOAD_JOB_UNKNOWN_ERROR = 'UPLOAD_JOB_WATCH_LOST'

async function throwIfUploadFailed(response: Response): Promise<void> {
  if (response.ok) return
  let detail: string | undefined
  try {
    const body = await response.json()
    detail = typeof body?.detail === 'string' ? body.detail : undefined
  } catch {
    // response body wasn't JSON - fall through to generic message
  }
  // Only 4xx details are curated for end users (e.g. "File type not allowed").
  // 5xx details are raw internal exception text and must never reach the UI.
  // The HTTP status itself is also withheld from the fallback - it's an
  // implementation detail, not something a user can act on.
  const safeDetail = response.status < 500 ? detail : undefined
  throw new Error(safeDetail || GENERIC_UPLOAD_ERROR)
}

/**
 * Shared fetch + failure-surfacing for all four upload endpoints. A network-level
 * failure (offline, CORS, DNS, an aborted signal) throws before a Response exists,
 * so its message is raw browser/runtime text - never safe to show a user - and is
 * normalised to the same generic message throwIfUploadFailed falls back to for an
 * unsafe/absent detail. Callers that cancel via AbortController check
 * `signal.aborted` themselves rather than inspecting this error's identity.
 */
async function postUploadJob(
  url: string,
  init: RequestInit,
): Promise<AsyncJobResponse> {
  let response: Response
  try {
    response = await fetch(url, init)
  } catch {
    throw new Error(GENERIC_UPLOAD_ERROR)
  }
  await throwIfUploadFailed(response)
  // A 2xx response can still fail to parse (malformed JSON from a proxy/backend
  // bug) or parse into something without a usable job_id - either throws a raw,
  // runtime-dependent message (SyntaxError text) that's never safe to show, or
  // leaves the caller polling a job that was never created. Both fail closed to
  // the same generic message rather than surfacing raw text or hanging silently.
  let data: unknown
  try {
    data = await response.json()
  } catch {
    throw new Error(GENERIC_UPLOAD_ERROR)
  }
  if (
    typeof data !== 'object' ||
    data === null ||
    typeof (data as { job_id?: unknown }).job_id !== 'string' ||
    !(data as { job_id: string }).job_id
  ) {
    throw new Error(GENERIC_UPLOAD_ERROR)
  }
  return data as AsyncJobResponse
}

export async function uploadFiles({
  token,
  files,
  orgUuid,
}: uploadFilesFn): Promise<AsyncJobResponse> {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))
  return postUploadJob(`${serverURL}/orgs/${orgUuid}/docs`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  })
}

export async function uploadWebsites({
  token,
  websites,
  orgUuid,
  singlePage = false,
  signal,
}: uploadWebsitesFn): Promise<AsyncJobResponse> {
  return postUploadJob(`${serverURL}/orgs/${orgUuid}/websites`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ websites: websites, single_page: singlePage }),
    signal,
  })
}

export async function uploadFundingNeedWebsites({
  token,
  websites,
  orgUuid,
  fundingNeedId,
}: uploadFundingNeedWebsitesFn): Promise<AsyncJobResponse> {
  return postUploadJob(
    `${serverURL}/funding-needs/${orgUuid}/${fundingNeedId}/websites`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ websites: websites }),
    }
  )
}

export async function uploadFundingNeedFiles({
  token,
  files,
  orgUuid,
  fundingNeedId,
}: uploadFundingNeedFilesFn): Promise<AsyncJobResponse> {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))
  return postUploadJob(
    `${serverURL}/funding-needs/${orgUuid}/${fundingNeedId}/docs`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  )
}

export interface UploadJobStatus {
  status: string
  progress?: number
  error?: string
  results?: WebsiteUploadResult[]
}

/**
 * One status read. Returns null for a job the server doesn't have — jobs expire
 * after 24h, and a job that's gone is an unknown outcome, not a failure. Any
 * other non-OK response throws so the caller can decide whether to retry.
 */
export async function getUploadJobStatus({
  token,
  jobId,
}: {
  token: string
  jobId: string
}): Promise<UploadJobStatus | null> {
  const response = await fetch(`${serverURL}/orgs/uploads/${jobId}/status`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (response.status === 404) return null
  if (!response.ok) throw new Error(GENERIC_UPLOAD_ERROR)
  const data = await response.json()
  return {
    status: data.status,
    progress: data.progress,
    error: data.error,
    results: Array.isArray(data.results) ? data.results : undefined,
  }
}

export async function waitForUploadJob({
  token,
  jobId,
  signal,
}: {
  token: string
  jobId: string
  signal?: AbortSignal
}): Promise<WebsiteUploadResult[]> {
  const startedAt = Date.now()
  // A poll failure is not a job failure. The job runs on the worker and finishes
  // regardless of whether anyone is watching, so one 502 from the proxy or one
  // 401 on an expiring token used to abandon a crawl that then completed fine —
  // and the wizard reported every page in the batch as failed. Only a terminal
  // job status ends the wait; transient failures are retried.
  let failingSince: number | null = null
  let intervalMs = POLL_INTERVAL_MS

  while (true) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
    if (Date.now() - startedAt > UPLOAD_JOB_TIMEOUT_MS) {
      throw new Error(UPLOAD_JOB_UNKNOWN_ERROR)
    }

    let data: { status?: string; results?: unknown } | null = null
    try {
      const response = await fetch(`${serverURL}/orgs/uploads/${jobId}/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      })
      if (response.ok) {
        data = await response.json()
      } else if (response.status === 404) {
        // The job store keeps entries for 24h, so a 404 means the id is genuinely
        // unknown rather than not-ready-yet. Retrying cannot help.
        throw new Error(GENERIC_UPLOAD_ERROR)
      }
    } catch (err) {
      if (signal?.aborted) throw err
      if (err instanceof Error && err.message === GENERIC_UPLOAD_ERROR) throw err
      // Network error — fall through to the retry below.
    }

    if (data) {
      failingSince = null
      if (data.status === 'completed') {
        return Array.isArray(data.results) ? data.results : []
      }
      if (data.status === 'error' || data.status === 'cleaned_up') {
        // data.error is the worker's raw exception text (app/worker.py) - never
        // curated for end users, so always fall back to the generic message
        // rather than passing it through (currently dead in the UI - no caller
        // renders this message today - but kept safe in case one starts to).
        throw new Error(GENERIC_UPLOAD_ERROR)
      }
    } else {
      failingSince ??= Date.now()
      if (Date.now() - failingSince > POLL_FAILURE_GRACE_MS) {
        // Sustained failure. The job may well still be running, so the caller
        // must not report the pages as failed — see UPLOAD_JOB_UNKNOWN_ERROR.
        throw new Error(UPLOAD_JOB_UNKNOWN_ERROR)
      }
    }

    // Back off once the job is clearly long-running: a crawl takes minutes, and
    // a poll a second for twenty of them is ~1200 requests, each opening a DB
    // connection through validate_job_access. Computed outside the success
    // branch so a failure streak backs off too rather than hammering at 1s.
    if (Date.now() - startedAt > POLL_BACKOFF_AFTER_MS) {
      intervalMs = POLL_INTERVAL_SLOW_MS
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
}

interface deleteFileFn {
  token: string
  docId: number
}
interface deleteFundingNeedFileFn {
  token: string
  docId: number
  needId: number
}

export async function deleteFile({ token, docId }: deleteFileFn) {
  const response = await fetch(`${serverURL}/orgs/docs/${docId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error('Failed to delete file')
  }

  return true
}

interface refetchWebsiteFn {
  token: string
  docId: number
  fullCrawl?: boolean
}

export async function refetchWebsite({
  token,
  docId,
  fullCrawl = false,
}: refetchWebsiteFn): Promise<WebsiteUploadResult> {
  const response = await fetch(
    `${serverURL}/orgs/docs/${docId}/refetch?full_crawl=${fullCrawl}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  return await response.json()
}

interface updateOrgWebsiteFn {
  token: string
  orgUuid: UUID
  website: string
}

export async function updateOrgWebsite({
  token,
  orgUuid,
  website,
}: updateOrgWebsiteFn): Promise<{ uuid: string; website: string }> {
  const response = await fetch(`${serverURL}/orgs/${orgUuid}/website`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ website }),
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  return await response.json()
}

export async function deleteFundingNeedFile({
  token,
  docId,
  needId,
}: deleteFundingNeedFileFn) {
  const response = await fetch(
    `${serverURL}/funding-needs/docs/${docId}/${needId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    throw new Error('Failed to delete file')
  }

  return true
}

export async function fetchOrgFundingNeedDocs(
  token: string,
  orgUuid: UUID,
  needId: number | null
): Promise<OrgDocResponse[]> {
  const response = await fetch(
    `${serverURL}/funding-needs/${orgUuid}/${needId}/docs`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  const docs = await response.json()
  return docs
}

export interface ActiveUpload {
  job_id: string
  status: string
  progress: number
  task?: string | null
  start_time?: string | null
}

/** The website upload currently running for this org, or null.
 *
 * Lets the wizard re-attach to a job it stopped watching, instead of leaving
 * pages showing a failure for work that is still going. Mirrors the scan
 * flow's fetchActiveScanForNeed — the server holds the state, nothing is
 * stored client-side, so a second tab or another device works the same way. */
export async function fetchActiveUpload({
  token,
  orgUuid,
  signal,
}: {
  token: string
  orgUuid: string
  signal?: AbortSignal
}): Promise<ActiveUpload | null> {
  const response = await fetch(`${serverURL}/orgs/${orgUuid}/uploads/active`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  })
  if (!response.ok) return null
  return (await response.json()) ?? null
}
